import { useEffect, useState } from "react";
import { GraduationCap, X as XIcon } from "lucide-react";

const STORAGE_PREFIX = "memso.teach.dismissed.";

/** A small dismissable teaching banner. Use for first-time-here
 *  microcopy on anchors that don't already have a welcome surface.
 *
 *  The dismissed state persists per-id in localStorage so the banner
 *  doesn't return after the user reads it once. New microcopy gets a
 *  new id so users see fresh guidance after meaningful changes. */
export function TeachingNote({
  id,
  title,
  body,
  tone = "sky",
}: {
  id: string;
  title: string;
  body: string;
  tone?: "sky" | "violet" | "amber" | "emerald";
}) {
  const [dismissed, setDismissed] = useState<boolean>(true);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      setDismissed(window.localStorage.getItem(STORAGE_PREFIX + id) === "1");
    } catch {
      setDismissed(false);
    }
  }, [id]);

  const onDismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(STORAGE_PREFIX + id, "1");
    } catch {
      /* storage blocked: dismissal still works in-session */
    }
  };

  if (dismissed) return null;

  const tones: Record<string, string> = {
    sky: "from-sky-50/80 to-indigo-50/60 border-sky-200/70 dark:from-sky-900/20 dark:to-indigo-900/20 dark:border-sky-700/40",
    violet:
      "from-violet-50/80 to-fuchsia-50/60 border-violet-200/70 dark:from-violet-900/20 dark:to-fuchsia-900/20 dark:border-violet-700/40",
    amber:
      "from-amber-50/80 to-orange-50/60 border-amber-200/70 dark:from-amber-900/20 dark:to-orange-900/20 dark:border-amber-700/40",
    emerald:
      "from-emerald-50/80 to-teal-50/60 border-emerald-200/70 dark:from-emerald-900/20 dark:to-teal-900/20 dark:border-emerald-700/40",
  };
  const iconTones: Record<string, string> = {
    sky: "text-sky-600 dark:text-sky-300",
    violet: "text-violet-600 dark:text-violet-300",
    amber: "text-amber-600 dark:text-amber-300",
    emerald: "text-emerald-600 dark:text-emerald-300",
  };

  return (
    <section
      role="note"
      aria-label={title}
      className={`rounded-xl border bg-gradient-to-br ${tones[tone]} p-3 sm:p-3.5 backdrop-blur flex items-start gap-3`}
    >
      <GraduationCap
        className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5 ${iconTones[tone]}`}
        strokeWidth={2.25}
      />
      <div className="min-w-0 flex-1">
        <div className="text-xs sm:text-sm font-bold text-slate-900 dark:text-slate-100">
          {title}
        </div>
        <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 mt-0.5 leading-relaxed">
          {body}
        </p>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss teaching note"
        className="flex-shrink-0 w-6 h-6 rounded-md inline-flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-200/60 dark:hover:bg-slate-700/40"
      >
        <XIcon className="w-3.5 h-3.5" />
      </button>
    </section>
  );
}
